import React from 'react';
import DetailsBar from '../shared/DetailsBar';
import moment from 'moment';
import BackButton from '../shared/BackButton';
import HeadingPlaceholder from '../shared/HeadingPlaceholder';
import TextAreaView from '../shared/TextAreaView';
import ModalEnabled from '../extend/ModalEnabled';
import SVGTrash from '../../svg/Trash';
import SVGMail from '../../svg/Mail';
import { serviceAPI } from '../../InterfaceAPI/serviceAPI';
import SVGDownload from '../../svg/Download';
import ServiceClient from './ServiceClient';
import ServiceStaff from './ServiceStaff';
import ServiceMenu from './ServiceMenu';
import ServiceDeleteModal from '../service/ServiceDeleteModal';
import ServiceCharge from './ServiceCharge';
import {
  maskServiceDate,
  maskServiceStartTime,
  maskServiceEndTime,
  serviceTypeSwitch,
  maskMoney
} from '../../masks';
import { getOverrideClass } from '../../helpers/autofillOverride';
import zipToDistrict from '../../helpers/zipToDistrict';
import { getTips, getChefRate, getClientFee } from '../../helpers/getAutofillRates';
import { getStaffRequirements } from '../../helpers/validateOccasionStaffing';
import { calcTax, stringfyServerErrors } from '../../helpers/util';
import { taxRate, CHEF_ROLES, TRAVEL_FEE, OCCASION_STATUS_LABELS } from '../../const';
import ConfirmModal from '../shared/ConfirmModal';
import { get as _get } from 'lodash';
import { SERVICE_TYPES} from '../../const'

class ServiceOccasion extends ModalEnabled {
  state = { 
    zip_to_service_district: {},
    staffRequirements: null,
    chefRateAutofill: 0,
    sousChefRateAutofill: 0,
    waiterBartenderRateAutofill: 0,
    preventAutofill: false
  }

  detailBarProperties = [
    {
      label: 'Date & Time',
      className: 'p',
      combine: [
        { key: 'date', mask: maskServiceDate },
        { raw: ' ', className: '--new-line' },
        { key: 'start_time', mask: maskServiceStartTime },
        { key: 'end_time', mask: maskServiceEndTime }
      ]
    },
    { label: 'Type of Service', key: 'occasion_type', className: 'p' },
    { label: 'Number of Guests', key: 'guest_number', className: 'p' }
  ];

  componentDidMount() {
    serviceAPI
      .getServiceDistricts()
      .then((districts) => {
        // Map ZIP codes to Service Districts
        this.setState({
          zip_to_service_district: districts.service_districts.reduce((acc, cur) => {
            const district_name = cur.name;
            for(const subsection of cur.service_district_subsections) {
              for(const zip of subsection.zip_codes) {
                acc[zip] = { districtName: district_name, subsection: subsection.name }
              }
            }
            return acc
          }, {})
        });

        this.setState({ preventAutofill: true }) 

        if (this.props.service.address && !this.props.service.occasion_zip_code) {
          const newAddress = { occasion_zip_code: this.props.service.address.zip_code }
          this.props.updateService(newAddress)
        }

        if (!this.props.service.invited_chefs) {
          this.props.updateService({ invited_chefs: []})
        }

        this.setState({ preventAutofill: false }) 

      })
      .catch((err) => {
        this.props.addMessage({
          className: 'error',
          message: stringfyServerErrors(err)
        });
      });
  }

  componentDidUpdate(prevProps, prevState) {
    const prevService = prevProps.service
    const service = this.props.service
    const serviceFeesWatchList = [
      'guest_number',
      'start_time',
      'uses_extended_menu',
      'occasion_zip_code',
      'status'
    ]

    const occasionTitleWatchList = [
      'occasion_zip_code',
      'status',
      'client',
      'occasion_type'
    ]

    const staffAutofillWatchList = [
      'occasion_zip_code',
      'guest_number',
      'uses_extended_menu',
      'includes_cocktails'
    ]

    // make sure 'occasion_zip_code' gets set if it has not been overriden, since it 
    // is used to caluclate fees and rates.
    if (this.props.service.address && !this.props.service.occasion_zip_code) {
      const newAddress = { occasion_zip_code: this.props.service.address.zip_code }
      this.props.updateService(newAddress)
    }

    if (this.props.showRequiredStaff) {
      this.onFieldsChange(prevService, staffAutofillWatchList, (_) => {
        const service = this.props.service;
        const zip = service.occasion_zip_code;
        const district = this.getDistrictFromZip(zip);
        const guestCount = service.guest_number;
        const staffReqs = getStaffRequirements(district, guestCount, service.uses_extended_menu, service.includes_cocktails);
        this.setState({ staffRequirements: staffReqs })
        this.props.updateService({ required_staff: {
          chefs: staffReqs.requiredChefs,
          sous_chefs: staffReqs.requiredSousChefs,
          bartender_waiters: staffReqs.requiredBartenders
        }})
        if (service.invited_chefs && service.invited_chefs.length === 0) {
          this.props.updateService({}, { isStaffEdit: true})
        }
      })
    }

    if (!this.state.preventAutofill) {
      // 1. Trigger update fees if and only if virtual staff (required staff) counts change
      // 2. Use staff requirement counts for calculation, instead of actual invited staff
      if (this.virtualStaffCountsChanged(prevProps, prevState)) {
        this.updateClientFee()
        this.updateTotalTip()
        this.updateTravelFee()
      }

      this.onFieldsChange(prevService, serviceFeesWatchList, () => {
        this.updateClientFee()
        this.updateTotalTip()
        this.updateTravelFee()
        this.updateChefRate()
      })

      this.onFieldsChange(prevService, ['invited_chefs'], (field) => {
        this.updateChefTips()
        service.invited_chefs.forEach(invite => {
          if (!prevService[field] || !prevService[field].includes(invite)) {
            this.updateChefRate(invite)
          }
        })
      })
    }

    this.onFieldsChange(prevService, occasionTitleWatchList, (_) => {
      this.updateTitle()
    })
  }

  onFieldsChange(prevService, fields, handleChange) {
    const service = this.props.service
    // need all fields to be set in order to update auto-fill
    if (fields.reduce((acc, cur) => acc && (service[cur] || service[cur] === false), true)) {
      fields.forEach(field => {
        if (prevService[field] !== service[field]) {
          handleChange(field)
        }
      })
    }
  }

  updateTitle() {
    if (!this.props.autoTitle) return;
    const service = this.props.service
    const service_type = this.props.service.service_type
    let prefix = ''
    if (service_type === SERVICE_TYPES.COOKING_CLASS) {
      prefix = 'Cooking Class: '
    }
    const first_name = _get(service, 'client.first_name', '');
    const last_name = _get(service, 'client.last_name', '');
    const zip = service.occasion_zip_code || '';
    const type = service.occasion_type;
    const districtSubsection = _get(this.state, `zip_to_service_district[${zip}].subsection`, '');
    const status = OCCASION_STATUS_LABELS[service.status];
    const name = prefix + `${status} / ${first_name} ${last_name} / ${districtSubsection} / ${type}`;
    this.props.updateService({ name });
  }

  updateClientFee() {
    if (!this.props.autoPricing) return;
    if (this.props.service.override_fields.fee_cents) return;
    const extendedMenu = this.props.service.uses_extended_menu
    // check for manual override
    const zip = this.props.service.occasion_zip_code
    const district = this.getDistrictFromZip(zip)
    const guestCount = this.props.service.guest_number;
    const date = new Date(this.props.service.start_time);
    const invitedChefs = this.getVirtualStaff(this.state, this.props)
    let fee = getClientFee(district, guestCount, date, invitedChefs, extendedMenu)
    if (fee) {
      if (this.props.service.status === "client_cancelled") {
        if (this.isLessThan48HoursAway()) {
          // if service has been canceled less than 48 hours away, but more than 24, cut
          // fee in half, otherwise if less than 24 hours away, full fee is charged.
          if(!this.isLessThan24HoursAway()) {
            fee = Math.ceil(fee * .5)
          }
        } else {
          // fee set to 0 if more than 48 hours away.
          fee = 0
        }
      }

      const tax_cents = calcTax(fee, taxRate);
      this.props.updateService({ fee_cents: fee, tax_cents });
    }
  }

  updateChefRate(updatedInvite) {
    if (!this.props.autoPricing) return;
    const zip = this.props.service.occasion_zip_code
    const district = this.getDistrictFromZip(zip)
    const guestCount = this.props.service.guest_number;
    const date = new Date(this.props.service.start_time);
    const invitedChefs = this.props.service.invited_chefs
    let changed = false;

    const newInvitedChefs = invitedChefs.map(invite => {
      if (
        (!updatedInvite || updatedInvite === invite) &&
          !this.props.service.override_fields[
            `invited_chef.${invite.chef.id}.rate`
          ]
      ) {
        let newRate = getChefRate(district, guestCount, date, invite.chef.vip, invite.role);
        if (this.props.service.status === "client_cancelled") {
          if (this.isLessThan48HoursAway()) {
            if(!this.isLessThan24HoursAway()) {
              newRate = Math.ceil(newRate * .5)
            }
          } else {
            newRate = '0'
          }
        }
        if (invite.rate !== newRate) {
          invite.rate = newRate;
          changed = true;
        }
      }
      return invite
    })

    /* Compute default rates for staff placeholders */
    this.setState({
      chefRateAutofill: getChefRate(district, guestCount, date, 0, CHEF_ROLES['CHEF']),
      sousChefRateAutofill: getChefRate(district, guestCount, date, 0, CHEF_ROLES['SOUS_CHEF']),
      waiterBartenderRateAutofill: getChefRate(district, guestCount, date, 0, CHEF_ROLES['WAITER']),
    })

    if (changed) {
      this.props.updateService({ invited_chefs: newInvitedChefs });
    }
  }

  updateTravelFee() {
    if (!this.props.autoPricing) return;
    if (this.props.service.override_fields.travel_cents) return;
    let travel_cents = 0
    if (this.props.service.status !== "client_cancelled") {
      travel_cents = this.getVirtualStaff(this.state, this.props).length * TRAVEL_FEE
    }
    this.props.updateService({ 
      travel_cents
    });
  }

  // update the total tip
  updateTotalTip() {
    if (!this.props.autoPricing) return;
    const extendedMenu = this.props.service.uses_extended_menu
    const zip = this.props.service.occasion_zip_code
    const district = this.getDistrictFromZip(zip)
    const guestCount = this.props.service.guest_number;
    const date = new Date(this.props.service.start_time);

    const {
      chefTip,
      sousChefTip,
      bartenderTip
    } = getTips(district, guestCount, date, extendedMenu)

    const virtualStaff = this.getVirtualStaff(this.state, this.props);
    let tipTotal = 0
    if (this.props.service.status !== "client_cancelled") {
      tipTotal = virtualStaff.reduce((acc, invite) => {
        switch(invite.role) {
          case CHEF_ROLES['CHEF']:
            return chefTip * 100 + acc;
          case CHEF_ROLES['SOUS_CHEF']:
            return sousChefTip * 100 + acc;
          case CHEF_ROLES['WAITER']:
          case CHEF_ROLES['BARTENDER']:
            return bartenderTip * 100 + acc;
          default:
        }
        return acc;
      }, 0)
    }

    tipTotal = Math.floor(tipTotal)
    if (!this.props.service.override_fields.tip_cents) {
      this.props.updateService({ tip_cents: tipTotal });
    }
  }

  // update tips of individually invited chefs
  updateChefTips() {
    if (!this.props.autoPricing) return;
    const extendedMenu = this.props.service.uses_extended_menu
    const zip = this.props.service.occasion_zip_code
    const district = this.getDistrictFromZip(zip)
    const guestCount = this.props.service.guest_number;
    const date = new Date(this.props.service.start_time);
    const invitedChefs = this.props.service.invited_chefs;
    const overrideFields = this.props.service.override_fields; 

    const {
      chefTip,
      sousChefTip,
      bartenderTip
    } = getTips(district, guestCount, date, extendedMenu)

    let changed = false;
    const updatedInvites = invitedChefs.map(invite => {
      if (overrideFields[`invited_chef.${invite.chef.id}.tip_cents`]) {
        return invite
      }
      let newTipCents = 0; 
      if (this.props.service.status !== "client_cancelled") {
        switch(invite.role) {
          case CHEF_ROLES['CHEF']:
            newTipCents = chefTip * 100;
            break;
          case CHEF_ROLES['SOUS_CHEF']:
            newTipCents = sousChefTip * 100;
            break;
          case CHEF_ROLES['WAITER']:
          case CHEF_ROLES['BARTENDER']:
            newTipCents = bartenderTip * 100;
            break;
          default:
        }
      }
      newTipCents = Math.floor(newTipCents)
      if (newTipCents !== invite.tip_cents) {
        changed = true
        return { ...invite, tip_cents: newTipCents };
      }
      return invite;
    })

    if (changed) {
      this.props.updateService({ invited_chefs: updatedInvites });
    }
  }

  // get fake staff array based on staff requirements
  getVirtualStaff(state, props) {
    const staffReqs = state.staffRequirements;
    const virtualStaff = [];

    const counts = this.getStaffCounts(props.service.invited_chefs);
    if (staffReqs) {
      this.createVirtualStaff(
        CHEF_ROLES['BARTENDER'],
        Math.max(staffReqs.requiredBartenders, counts.waiterBartenderCount),
        virtualStaff
      );
      this.createVirtualStaff(
        CHEF_ROLES['SOUS_CHEF'],
        Math.max(staffReqs.requiredSousChefs, counts.sousChefCount),
        virtualStaff
      );
      this.createVirtualStaff(
        CHEF_ROLES['CHEF'],
        Math.max(staffReqs.requiredChefs, counts.chefCount),
        virtualStaff
      );
    }
    return virtualStaff
  }

  getStaffCounts(invites) {
    invites = invites || []
    return {
      chefCount: invites.reduce((acc, chef) => 
        acc + (chef.role === CHEF_ROLES["CHEF"] ? 1 : 0) , 0),
      sousChefCount: invites.reduce((acc, chef) => 
        acc + (chef.role === CHEF_ROLES["SOUS_CHEF"] ? 1 : 0) , 0),
      waiterBartenderCount: invites.reduce((acc, chef) => 
        acc + (chef.role === CHEF_ROLES["BARTENDER"] || chef.role === CHEF_ROLES["WAITER"] ? 1 : 0) , 0)
    }
  }

  virtualStaffCountsChanged(prevProps, prevState) {
    const prevCounts = this.getStaffCounts(this.getVirtualStaff(prevState, prevProps))
    const curCounts = this.getStaffCounts(this.getVirtualStaff(this.state, this.props))

    if (prevCounts.chefCount !== curCounts.chefCount) return true;
    if (prevCounts.sousChefCount !== curCounts.sousChefCount) return true;
    if (prevCounts.waiterBartenderCount !== curCounts.waiterBartenderCount) return true;

    return false
  }

  createVirtualStaff(role, count, staffArray) {
    for(let i = 0; i < count; i++) {
      staffArray.push({ role })
    }
  }

  isLessThan24HoursAway = () => moment(this.props.service.true_start_time).diff(moment().add(24, 'hours')) < 0
  isLessThan48HoursAway = () => moment(this.props.service.true_start_time).diff(moment().add(48, 'hours')) < 0
  getDistrictFromZip = (zip) => zipToDistrict(zip)

  render() {
    const {
      service,
      updateService,
      setServiceUnsaved,
      updateMenuSelections,
      openModal,
      isClientEdit,
      isMenuEdit,
      isStaffEdit,
      sendStaffMail,
      downloadChefMaterials,
      isDownloading,
      autoPricing
    } = this.props;

    const zipOverride = service.occasion_zip_code

    return (
      <React.Fragment>
        <div className="cms--service">
          <div className="cms__head cms__head--padding">
            <div className="cms__content">
              <BackButton defaultUrl="/admin/services" />
              <div className="cms__bar">
                <HeadingPlaceholder
                  value={service.name}
                  placeholder={serviceTypeSwitch(service.service_type)}
                  label={serviceTypeSwitch(service.service_type)}
                />
                <div className="cms__bar-ctas">
                  {service.id && !isDownloading ? (
                    <div
                      className="btn-icon btn-icon--download"
                      onClick={downloadChefMaterials}
                    >
                      <SVGDownload />
                    </div>
                  ) : (
                    ''
                  )}
                  {service.id && isDownloading ? (
                    <div className="p">downloading...</div>
                  ) : (
                    ''
                  )}
                  {service.id ? (
                    <div
                      className="btn-icon btn-icon--mail"
                      onClick={() => this.props.openSendDetailsModal()}
                    >
                      <SVGMail />
                    </div>
                  ) : (
                    ''
                  )}
                  {service.id ? (
                    <div
                      className="btn-icon btn-icon--trash"
                      onClick={() => { this.openDelete() }}
                    >
                      <SVGTrash />
                    </div>
                  ) : (
                    ''
                  )}
                  {process.env.REACT_APP_SHOW_SMS_REMINDER_BUTTON && (
                    <div
                      className="btn btn--hollow-blue"
                      onClick={() => openModal('send-menu-sms-reminder')}
                    >
                      Send SMS
                    </div>
                  )}
                  <div
                    className="btn btn--hollow-blue"
                    onClick={() => openModal('edit-details')}
                  >
                    Edit Details
                  </div>
                  <input
                    type="submit"
                    className="btn btn--solid-blue"
                    value="Save"
                    onClick={() => this.checkClientThenSubmit()}
                  />
                </div>
              </div>
              <DetailsBar data={service} columns={this.detailBarProperties} />
            </div>
          </div>

          <div className="cms__body">
            <div className="cms__content">
              <ServiceClient
                openModal={() => openModal('add-client')}
                isEdit={isClientEdit}
                client={service.client}
                address={service.address}
                updateService={updateService}
              />

              <ServiceMenu
                openDishModal={() => openModal('add-dish')}
                openMenuModal={() => openModal('add-menu')}
                openGroceryList={this.props.openGroceryList}
                setServiceUnsaved={setServiceUnsaved}
                service={service}
                updateService={updateService}
                updateMenuSelections={updateMenuSelections}
                isEdit={isMenuEdit}
              />

              <ServiceStaff
                openAddStaffModal={(defaultRole) => {
                  this.props.updateService({ defaultRole }, { zipOverride })
                  openModal('add-staff')}
                }
                showOverrides={autoPricing}
                sendStaffMail={sendStaffMail}
                service={service}
                updateService={updateService}
                staffRequirements={this.state.staffRequirements}
                isEdit={isStaffEdit}
                showTips={true}
                autoFillRates={{
                  chef: this.state.chefRateAutofill,
                  sousChef: this.state.sousChefRateAutofill,
                  waiterBartender: this.state.waiterBartenderRateAutofill
                }}
                openSendDetailsModal={staff => 
                  this.props.openSendDetailsModal(staff)
                }
              />

              <div className="row">
                <div className="row__item row__item--half row__item--pad-right">
                  <ServiceCharge
                    service={service}
                    showOverrides={autoPricing}
                    updateService={updateService}
                  />
                </div>
                <div className="row__item row__item--half row__item--pad-left">
                  <TextAreaView
                    value={service.notes}
                    openEdit={() => openModal('notes')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <ServiceDeleteModal
          isOpen={this.isOpen('delete-occasion')}
          closeModal={this.closeModal}
          service={service}
          deleteService={this.props.deleteService}
        >
          {this.getConfirmDeleteBody()}
        </ServiceDeleteModal>
        <ConfirmModal
          isOpen={this.isOpen('occasion-validation')}
          closeModal={this.closeModal}
          onSubmit={this.confirmSaveService}
          heading={"Service Confirmation"}
          isAdmin={true}
          cta={"Confirm"}
          body={''}
        >
          {this.renderValidationModal()}
        </ConfirmModal>
      </React.Fragment>
    );
  }

  getCurrentStaffCounts = () => {
    const service = this.props.service
    return service.invited_chefs.reduce((acc, cur) => {
      switch(cur.role) {
        case CHEF_ROLES['CHEF']:
          acc.chefCount += 1;
          break;
        case CHEF_ROLES['SOUS_CHEF']:
          acc.sousChefCount += 1;
          break;
        case CHEF_ROLES['BARTENDER']:
        case CHEF_ROLES['WAITER']:
          acc.waiterBartenderCount += 1;
          break;
        default:
      }
      return acc
    }, {chefCount: 0, sousChefCount: 0, waiterBartenderCount: 0 })
  }

  renderValidationModal = () => {
    const staffAlerts = []
    const service = this.props.service
    if (service && service.address && service.invited_chefs) {
      const zip = this.props.service.occasion_zip_code
      const district = this.getDistrictFromZip(zip)
      const guestCount = service.guest_number || 0;
      const {chefCount, sousChefCount, waiterBartenderCount } = this.getCurrentStaffCounts()
      
      const { requiredChefs, requiredSousChefs, requiredBartenders } = getStaffRequirements(district, guestCount, service.uses_extended_menu, service.includes_cocktails);
      if (chefCount < requiredChefs) {
        staffAlerts.push(`This service does not have the required amount of chefs: ${chefCount} invited but ${requiredChefs} required.`)
      }
      if (sousChefCount < requiredSousChefs) {
        staffAlerts.push(`This service does not have the required amount of sous chefs: ${sousChefCount} invited but ${requiredSousChefs} required.`)
      }
      if (waiterBartenderCount < requiredBartenders) {
        staffAlerts.push(`This service does not have the required amount of waiters and/or bartenders: ${waiterBartenderCount} invited but ${requiredBartenders} required.`)
      }
    }
    return (
      <div>
        {staffAlerts.length > 0 &&
          (
            <div>
              <div>
                <p className="p p--bold p--red">WARNING INSUFFICIENT STAFF:</p>
                {staffAlerts.map((alert, i) => {
                  return (<p key={i} className="p p--red">{alert}</p>)
                })}
              </div>
              <br/>
            </div>
          )}
          <div>
            <p className="p p--bold">Client Pricing Breakdown</p>
            <table className="table">
              <tbody>
                <tr>
                  <td><p className="p p--bold">Fee:</p></td>
                  <td>
                    <p className={`p ${getOverrideClass(service, 'fee_cents')}`}>
                      {maskMoney(service.fee_cents || 0)}
                    </p>
                  </td>
                </tr>
                <tr>
                  <td><p className="p p--bold">Tax:</p></td>
                  <td><p className='p'>{maskMoney(service.tax_cents || 0)}</p></td>
                </tr>
                <tr>
                  <td><p className="p p--bold">Estimated Grocery Cost:</p></td>
                  <td><p className='p'>{maskMoney(service.est_grocery_cents || 0)}</p></td>
                </tr>
                <tr>
                  <td><p className="p p--bold">Tip:</p></td>
                  <td>
                    <p className={`p ${getOverrideClass(service, 'tip_cents')}`}>
                      {maskMoney(service.tip_cents || 0)}
                    </p>
                  </td>
                </tr>
                <tr>
                  <td><p className="p p--bold">Travel:</p></td>
                  <td>
                    <p className={`p ${getOverrideClass(service, 'travel_cents')}`}>
                      {maskMoney(service.travel_cents || 0)}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
      </div>
    )
  }

  confirmSaveService = () => {
    this.props.submitService();
  }

  openDelete = () => {
    this.openModal('delete-occasion')
  }

  getConfirmDeleteBody = () => {
    const { service } = this.props;
    if (service.invited_chefs) {
      const confirmedChefs = service.invited_chefs.filter(invite => invite.status === 'accepted')
      if (confirmedChefs.length > 0) {
        return (
          <div class="p--red">
            Warning: The following chefs have already confirmed: {confirmedChefs.map(invite => invite.chef.name).join(', ')}
          </div>
          )
      }
    }
    return ''
  }

  checkClientThenSubmit = () => {
    const { service, showRequiredStaff } = this.props;
    if (!service.client) {
      // require client before saving
      this.props.addMessage({
        className: 'error',
        message: 'You must select a client before saving'
      });
    } else {
      if (showRequiredStaff) {
        this.openModal('occasion-validation')
      } else {
        this.props.submitService();
      }
    }
  };
}
export default ServiceOccasion;



// WEBPACK FOOTER //
// ./src/components/service/ServiceOccasion.js